import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';
import apiClient from '../config/axios';
import previewText from '../utils/suggestionPreview';
import entityTypeIcon from '../utils/entityIcons';

const SearchAutocomplete = ({ value, onChange, placeholder, apiEndpoint, basePath, onSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hintVisible, setHintVisible] = useState(false);
  const abortRef = useRef(null);
  const wrapperRef = useRef(null);
  // Whether the person has typed since their last search (issue 1236): a value restored from the address — a reload,
  // Back — or a search just made never opens the list by itself, and neither does one in a box without the focus.
  const typedRef = useRef(false);

  const debouncedQuery = useDebounce(value, 300);

  useEffect(() => {
    if (debouncedQuery.trim().length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    apiClient.get(`${apiEndpoint}?q=${encodeURIComponent(debouncedQuery.trim())}&limit=8`, {
      signal: controller.signal
    })
      .then((res) => {
        setSuggestions(res.data.suggestions || []);
        const focused = wrapperRef.current && wrapperRef.current.contains(document.activeElement);
        if (typedRef.current && focused) setIsOpen(true);
        setActiveIndex(-1);
      })
      .catch((err) => {
        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
          console.error('Autocomplete error:', err);
        }
      });

    return () => controller.abort();
  }, [debouncedQuery, apiEndpoint]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectSuggestion = (item) => {
    setIsOpen(false);
    setSuggestions([]);
    onSelect?.(item);
    // Query-param addressing: detail is the list route + ?slug= selector. The
    // suggestion's slug resolves to the canonical printing (Finding E); rulebook
    // is included when the suggest payload carries one.
    const qs = item.rulebook_slug
      ? `?slug=${item.slug}&rulebook=${item.rulebook_slug}`
      : `?slug=${item.slug}`;
    navigate(`${basePath}${qs}`, { state: { from: `${location.pathname}${location.search}` } });
  };

  const handleKeyDown = (e) => {
    // Enter searches: until they type again, the suggestions stay shut (issue 1236).
    if (e.key === 'Enter') { typedRef.current = false; if (activeIndex < 0) setIsOpen(false); }
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div
      className="relative"
      ref={wrapperRef}
      onMouseEnter={() => setHintVisible(true)}
      onMouseLeave={() => setHintVisible(false)}
    >
      <Search className="search-icon" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => { typedRef.current = true; onChange(e.target.value); }}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
        className="search-input"
        role="combobox"
        aria-controls="searchautocomplete-listbox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-activedescendant={activeIndex >= 0 ? `suggestion-${activeIndex}` : undefined}
        autoComplete="off"
      />
      {isOpen && suggestions.length > 0 && (
        <ul id="searchautocomplete-listbox" className="autocomplete-dropdown" role="listbox">
          {suggestions.map((item, index) => {
            // Rich rows (issue 190): a type icon plus the derived one-line
            // preview under the name. entityType is the list route's own
            // segment — basePath is always `/<segment>`.
            const entityType = (basePath || '').replace(/^\//, '');
            const Icon = entityTypeIcon(entityType);
            const preview = previewText(entityType, item.preview);
            return (
              <li
                key={item.slug}
                id={`suggestion-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className="px-3 py-2 text-sm cursor-pointer transition-colors duration-100"
                style={
                  index === activeIndex
                    ? { background: 'var(--gold-dim)', color: 'var(--gold)' }
                    : { color: 'var(--text-primary)' }
                }
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(item); }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block truncate">{item.name}</span>
                    {preview && (
                      <span className="block truncate text-xs" style={{ color: 'var(--text-muted)' }}>
                        {preview}
                      </span>
                    )}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {/* Wildcard hint (issue #317): hover-only by product decision (the site
          features guide covers touch devices), and only while the input is
          EMPTY so it never competes with the suggestion dropdown for the same
          space. `role="note"` + aria-label give tests and assistive tech a
          stable accessible name. */}
      {hintVisible && !value && (
        <div
          role="note"
          aria-label="Search syntax"
          className="autocomplete-dropdown px-3 py-2 text-xs"
          style={{ color: 'var(--text-muted)' }}
        >
          Use <code>*</code> as a wildcard: <code>fire*</code> finds names starting
          with &ldquo;fire&rdquo;, <code>*bane</code> names ending in
          &ldquo;bane&rdquo;. Everything else matches literally.
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
